/* @flow */
import { ActionTypes } from 'constants/DeviceConstants';
import API from 'lib/RemindAPI';
import Device from 'entities/Device';
import { dispatcher } from 'lib/Dispatcher';
import type { CreateOutboundDeviceOptions } from 'entities/Device';

const DeviceActions = {
  loadDevices(ids?: number[]) {
    const all = ids === undefined;
    const params = all ? undefined : { ids };

    return API.request('/devices/batch', 'POST', params).then(result => {
      const devices = result.devices.map(device => Device.fromJSON(device));

      dispatcher.dispatch({
        type: ActionTypes.LOAD_DEVICES,
        devices,
        all,
      });

      return devices;
    });
  },

  /* Enables a user's main email device.
   * It kinda like DeviceActions.enable(emailDevice)
   * but this will create the email device if none exist.
   */
  enableEmailDevice(): Promise<Device> {
    return Device.enableEmailDevice().then(device => {
      dispatcher.dispatch({
        type: ActionTypes.UPDATE_DEVICE,
        device,
      });

      return device;
    });
  },

  newEmailDevice(uid: string): Promise<Device> {
    return Device.createEmailDevice(uid).then(device => {
      dispatcher.dispatch({
        type: ActionTypes.CREATE_DEVICE,
        device,
      });

      return device;
    });
  },

  deleteEmailDevice(device: Device): Promise<Device> {
    return device.delete().then(() => {
      dispatcher.dispatch({
        type: ActionTypes.DELETE_DEVICE,
        device,
      });

      return device;
    });
  },

  /*
   * Begin a outbound device verification process, the resolved
   * promise will return a token. You need to pass this token
   * and the code received by device to the createOutboundDevice function.
   */
  outboundVerify(address: string, receiveCodeOverVoice: boolean = false) {
    return Device.outboundVerify(address, receiveCodeOverVoice);
  },

  /* @param address - the device address returned by outboundVerify.
   * @param code    - the 4 digit code received by the device.
   * @param options - some options.
   */
  createOutboundDevice(address: string, code: string, options: CreateOutboundDeviceOptions = {}) {
    return Device.createOutboundDevice(address, code, options).then(response => {
      dispatcher.dispatch({
        type: ActionTypes.UPDATE_DEVICE,
        device: response.device,
      });

      return response;
    });
  },

  localDeleteAll(): Promise<void> {
    dispatcher.dispatch({
      type: ActionTypes.DELETE_ALL_DEVICES,
    });

    return new Promise(resolve => resolve());
  },

  /* local delete to remove the device from the store
   * this is because the API destroy the sms device by itself
   * once we add a new sms device.
   */
  localDelete(device: Device): Promise<Device> {
    dispatcher.dispatch({
      type: ActionTypes.DELETE_DEVICE,
      device,
    });

    return new Promise(resolve => resolve(device));
  },

  enable(deviceToEnable: Device) {
    return deviceToEnable.enable().then(device => {
      dispatcher.dispatch({
        type: ActionTypes.UPDATE_DEVICE,
        device,
      });
    });
  },

  disable(deviceToDisable: Device) {
    return deviceToDisable.disable().then(device => {
      dispatcher.dispatch({
        type: ActionTypes.UPDATE_DEVICE,
        device,
      });
    });
  },
};

export default DeviceActions;
